/**
 * AdvancedOptions Component
 * Zaawansowane opcje dla generacji video (collapsed by default)
 */

import React, { useState } from 'react';
import './AdvancedOptions.css';

interface AdvancedOptionsProps {
  sizeOptions: string[];
  durationOptions: number[];
  defaultParams: {
    num_inference_steps: number;
    guidance: number;
    size: string;
    duration: number;
    flow_shift: number;
    enable_prompt_optimization: boolean;
    enable_safety_checker: boolean;
  };
  values: {
    duration: number;
    size: string;
    num_inference_steps: number;
    guidance: number;
    flow_shift: number;
    seed: number;
    enable_safety_checker: boolean;
  };
  onChange: (field: string, value: any) => void;
}

export const AdvancedOptions: React.FC<AdvancedOptionsProps> = ({
  sizeOptions,
  durationOptions,
  defaultParams,
  values,
  onChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="advanced-options">
      <button
        type="button"
        className="advanced-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? '▼' : '▶'} Advanced Options
      </button>

      {isExpanded && (
        <div className="advanced-content">
          {/* Duration */}
          <div className="form-group">
            <label htmlFor="duration">
              Duration (seconds)
              <span className="help-text">Duration in seconds</span>
            </label>
            <select
              id="duration"
              value={values.duration}
              onChange={(e) => onChange('duration', parseInt(e.target.value))}
            >
              {durationOptions.map((dur) => (
                <option key={dur} value={dur}>
                  {dur}s
                </option>
              ))}
            </select>
          </div>

          {/* Size */}
          <div className="form-group">
            <label htmlFor="size">
              Size
              <span className="help-text">Video resolution</span>
            </label>
            <select
              id="size"
              value={values.size}
              onChange={(e) => onChange('size', e.target.value)}
            >
              {sizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          {/* Number of Inference Steps */}
          <div className="form-group">
            <label htmlFor="num_inference_steps">
              Number of Inference Steps
              <span className="help-text">
                More steps generally produce higher quality
              </span>
            </label>
            <input
              type="number"
              id="num_inference_steps"
              min="10"
              max="100"
              value={values.num_inference_steps}
              onChange={(e) =>
                onChange('num_inference_steps', parseInt(e.target.value))
              }
            />
          </div>

          {/* Guidance Scale (CFG) */}
          <div className="form-group">
            <label htmlFor="guidance">
              Guidance Scale (CFG)
              <span className="help-text">
                Higher values follow the prompt more closely
              </span>
            </label>
            <input
              type="number"
              id="guidance"
              min="1"
              max="20"
              step="0.5"
              value={values.guidance}
              onChange={(e) =>
                onChange('guidance', parseFloat(e.target.value))
              }
            />
          </div>

          {/* Flow Shift */}
          <div className="form-group">
            <label htmlFor="flow_shift">
              Flow Shift
              <span className="help-text">
                The shift value for the timestep schedule for flow matching
              </span>
            </label>
            <input
              type="number"
              id="flow_shift"
              min="1"
              max="10"
              value={values.flow_shift}
              onChange={(e) =>
                onChange('flow_shift', parseInt(e.target.value))
              }
            />
          </div>

          {/* Seed */}
          <div className="form-group">
            <label htmlFor="seed">
              Seed
              <span className="help-text">
                Random seed (-1 for random)
              </span>
            </label>
            <input
              type="number"
              id="seed"
              value={values.seed}
              onChange={(e) => onChange('seed', parseInt(e.target.value))}
            />
          </div>

          {/* Safety Checker */}
          <div className="form-group checkbox-group">
            <label htmlFor="enable_safety_checker">
              <input
                type="checkbox"
                id="enable_safety_checker"
                checked={values.enable_safety_checker}
                onChange={(e) =>
                  onChange('enable_safety_checker', e.target.checked)
                }
              />
              <span>Enable Safety Checker</span>
            </label>
            <span className="help-text">
              Whether to enable the safety checker
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
